import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { loginUser, logoutUser, refreshToken } from "../api/auth";
import { getCurrentUser, mergeJwtAndProfile } from "../api/users";
import { setUpInterceptors } from "../api/axios";

export const AuthContext = createContext();

const parseJWT = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    access_token: localStorage.getItem("access_token"),
    refresh_token: localStorage.getItem("refresh_token"),
    user: parseJWT(localStorage.getItem("access_token")),
  });

  const [ready, setReady] = useState(false);
  const profileAbortRef = useRef(null);

  const loadUserWithProfile = useCallback(async (accessToken) => {
    if (!accessToken) return null;
    const jwt = parseJWT(accessToken);
    if (profileAbortRef.current) profileAbortRef.current.abort();
    const controller = new AbortController();
    profileAbortRef.current = controller;
    try {
      const profile = await getCurrentUser({ signal: controller.signal });
      return mergeJwtAndProfile(jwt, profile);
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") return jwt;
      console.warn("Could not load user profile; using token claims only.", err);
      return jwt;
    }
  }, []);

  const login = async (username, password) => {
    try {
      const tokens = await loginUser(username, password);
      localStorage.setItem("access_token", tokens.access);
      localStorage.setItem("refresh_token", tokens.refresh);
      const user = await loadUserWithProfile(tokens.access);
      setAuth({
        access_token: tokens.access,
        refresh_token: tokens.refresh,
        user: user ?? parseJWT(tokens.access),
      });
    } catch (err) {
      if (err.response && err.response.data) {
        throw err;
      } else {
        throw new Error("Network / Server Error");
      }
    }
  };

  const logout = useCallback(async () => {
    if (profileAbortRef.current) {
      profileAbortRef.current.abort();
      profileAbortRef.current = null;
    }

    if (localStorage.getItem("refresh_token")) {
      try {
        await logoutUser(localStorage.getItem("refresh_token"));
      } catch (err) {
        console.log(err);
      }
    }

    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setAuth({ access_token: null, refresh_token: null, user: null });
  }, []);

  const tryTokenRefresh = useCallback(async () => {
    if (localStorage.getItem("refresh_token")) {
      try {
        const data = await refreshToken(localStorage.getItem("refresh_token"));
        localStorage.setItem("access_token", data.access);
        localStorage.setItem("refresh_token", data.refresh);
        const user = await loadUserWithProfile(data.access);
        setAuth({
          access_token: data.access,
          refresh_token: data.refresh,
          user: user ?? parseJWT(data.access),
        });
      } catch (err) {
        logout();
        throw err;
      }
    } else {
      logout();
      throw new Error("No Refresh Token Available");
    }
  }, [logout, loadUserWithProfile]);

  useEffect(() => {
    const interval = setInterval(() => {
      tryTokenRefresh().catch(() => {});
    }, 4.5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [tryTokenRefresh]);

  useEffect(() => {
    setUpInterceptors(auth, logout, tryTokenRefresh);
    setReady(true);
  }, [auth, tryTokenRefresh, logout]);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return undefined;

    let cancelled = false;
    (async () => {
      const user = await loadUserWithProfile(token);
      if (!cancelled && user) {
        setAuth((prev) =>
          prev.access_token === token
            ? { ...prev, user }
            : prev
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadUserWithProfile]);

  return (
    <AuthContext.Provider value={{ auth, login, logout, ready }}>
      {children}
    </AuthContext.Provider>
  );
};
